import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { cn } from './cn.js';
import { Logo } from './Logo.js';
import { Spinner } from './Spinner.js';

/**
 * The sign-in screen shared by all three portals.
 *
 * Client, employee and admin are the same layout with different copy, so the
 * shell lives here once and each app passes only its own content. Anything
 * visual that differs between them is a prop, not a fork.
 *
 * Colours come from the `auth` scale in the Tailwind preset — see
 * packages/config/tailwind.preset.js.
 */

export interface AuthLayoutProps {
  /** Small gold caption above the heading — "CLIENT PORTAL". */
  badge: string;
  /** Two-line headline. Pass a fragment with <br /> to control the break. */
  headline: ReactNode;
  /** Supporting paragraph under the headline. */
  description: string;
  /** Four short capability lines with green ticks. */
  features: string[];
  /** Copy inside the card, above the form. */
  cardTitle: string;
  cardDescription: string;
  /** Reassurance line under the button. */
  footnote: string;
  /** The form itself. */
  children: ReactNode;
}

export function AuthLayout({
  badge,
  headline,
  description,
  features,
  cardTitle,
  cardDescription,
  footnote,
  children,
}: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen bg-auth-page">
      {/*
        Brand panel. Hidden below lg — on a phone it would push the form
        below the fold, and signing in is the only job of this screen.
      */}
      <aside className="relative hidden w-[45%] shrink-0 flex-col justify-between bg-auth-panel px-14 py-9 lg:flex">
        <BrandMark size={77} />

        <div className="max-w-[380px]">
          <h1 className="text-[36px] font-bold leading-[1.15] tracking-tight text-auth-heading">
            {headline}
          </h1>

          <p className="mt-5 text-[13.5px] leading-[1.7] text-auth-body">{description}</p>

          <ul className="mt-7 flex flex-col gap-3">
            {features.map((feature) => (
              <li key={feature} className="flex items-center gap-2.5">
                <span className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border border-auth-check/50">
                  <Check size={10} strokeWidth={3} className="text-auth-check" />
                </span>
                <span className="text-[13px] text-auth-body">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-[11px] text-auth-dim">
          © 2026 GoBeeVir Business OS · Enterprise CRM, Finance &amp; Automation
        </p>
      </aside>

      <main className="flex flex-1 items-center justify-center px-5 py-10">
        <div className="w-full max-w-[420px]">
          {/* The brand only appears here once the panel is gone. */}
          <div className="mb-6 flex justify-center lg:hidden">
            <BrandMark tone="light" size={76} />
          </div>

          <div className="rounded-xl border border-auth-line bg-auth-card px-8 py-8">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-auth-gold">
              {badge}
            </p>

            <h2 className="mt-3 text-[23px] font-bold tracking-tight text-auth-heading">
              {cardTitle}
            </h2>
            <p className="mt-2 text-[13px] leading-[1.6] text-auth-body">{cardDescription}</p>

            <div className="mt-6">{children}</div>

            <p className="mt-5 flex items-center justify-center gap-1.5 text-[11px] text-auth-dim">
              <ShieldCheck size={12} />
              {footnote}
            </p>
          </div>

          <p className="mt-6 text-center text-[11px] text-ink-soft lg:hidden">
            © 2026 GoBeeVir Business OS
          </p>
        </div>
      </main>
    </div>
  );
}

function BrandMark({
  tone = 'dark',
  size = 88,
}: {
  tone?: 'dark' | 'light';
  size?: number;
}) {
  // The artwork carries a navy beaver and a navy half-wordmark, drawn for paper
  // and light screens. On the dark panel those parts sink into the background
  // and only the orange 'B' survives — enlarging it makes the disappearance
  // more obvious rather than less.
  //
  // So on dark: show the mark and let the app draw 'GoBeeVir' in white beside
  // it. On light: the lockup exactly as the designer drew it.
  if (tone === 'dark') {
    return <Logo variant="mark" size={size} tone="dark" showWordmark wordmarkSize={21} />;
  }
  return <Logo variant="lockup" size={size} tone={tone} />;
}

// ── Form pieces ──────────────────────────────────────────────
// Dark-themed on purpose: the shared Input component is built for the light
// application surfaces and would need overriding on every prop here.

export interface AuthFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon: ReactNode;
  error?: string;
}

export const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(function AuthField(
  { label, icon, error, className, id, type, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  // Only password fields get a reveal control, and the toggle swaps the input
  // type rather than the value, so autofill and password managers still see a
  // password field.
  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-[12px] text-auth-body">
        {label}
      </label>

      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-auth-dim">
          {icon}
        </span>

        <input
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          type={isPassword && revealed ? 'text' : type}
          className={cn(
            'h-[42px] w-full rounded-lg border bg-auth-field pl-9 text-[13.5px] text-auth-heading',
            // Room for the reveal button, and hide Edge's own eye so there are
            // not two of them sitting on top of each other.
            isPassword ? 'pr-10 [&::-ms-reveal]:hidden' : 'pr-3',
            'placeholder:text-auth-dim',
            'transition-colors focus:outline-none focus:ring-2',
            error
              ? 'border-danger focus:border-danger focus:ring-danger/25'
              : 'border-auth-line focus:border-auth-gold focus:ring-auth-gold/25',
            className,
          )}
          {...rest}
        />

        {isPassword && (
          <button
            type='button'
            onClick={() => setRevealed((v) => !v)}
            // Not in the tab order: someone tabbing from password to the submit
            // button should reach the button, not a decoration in between.
            tabIndex={-1}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            className='absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded text-auth-dim transition-colors hover:text-auth-heading focus:outline-none focus-visible:ring-2 focus-visible:ring-auth-gold/40'
          >
            {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>

      {error && (
        <p id={`${fieldId}-error`} className="text-[11.5px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
});

export interface AuthButtonProps {
  children: ReactNode;
  isLoading?: boolean;
  type?: 'submit' | 'button';
  onClick?: () => void;
}

export function AuthButton({ children, isLoading = false, type = 'submit', onClick }: AuthButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isLoading}
      aria-busy={isLoading || undefined}
      className={cn(
        'relative h-[42px] w-full rounded-lg bg-auth-gold text-[13.5px] font-semibold text-auth-panel',
        'transition-colors hover:bg-auth-gold-hover',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-gold focus-visible:ring-offset-2 focus-visible:ring-offset-auth-card',
        'disabled:cursor-not-allowed disabled:opacity-70',
      )}
    >
      {isLoading && (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner size="sm" />
        </span>
      )}
      {/* Label keeps its box while loading so the button never changes width. */}
      <span className={cn(isLoading && 'opacity-0')}>{children}</span>
    </button>
  );
}

export function AuthLink({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[12px] text-auth-gold transition-colors hover:text-auth-gold-hover focus-visible:outline-none focus-visible:underline"
    >
      {children}
    </button>
  );
}

export function AuthError({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-[12.5px] text-red-300"
    >
      {children}
    </div>
  );
}
